"use client";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { usePostHog } from "@posthog/next";
import { useCallback, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import Icon from "@/components/library/Icon";
import { Partners } from "@/enums/agencies";
import {
  IntegrationListingStatus,
  IntegrationTab,
  integrationTabRoute,
  UnifiedIntegration,
} from "@/types/integrations";
import { getLetterAvatar } from "@/utils/integrations/integration-logos";
import { normalizeAlphaNum } from "@/utils/integrations/marketplace-config";
import { useRouter } from "@bprogress/next";

const AGENCY_ZOOM_KEY = normalizeAlphaNum(Partners.AgencyZoom);
const AGENCY_ZOOM_CALLS_ROUTE = `${integrationTabRoute(IntegrationTab.Partners)}/${Partners.AgencyZoom}/calls`;

export default function IntegrationCard({
  integration,
  onConnect,
  onComingSoon,
  onDisconnect,
  highlighted = false,
}: Readonly<{
  integration: UnifiedIntegration;
  onConnect: () => void;
  onComingSoon: () => void;
  onDisconnect: () => void;
  highlighted?: boolean;
}>) {
  const router = useRouter();
  const posthog = usePostHog();
  const { t: ct } = useTranslation("common");
  const { t } = useTranslation("partners");
  const [imgError, setImgError] = useState(false);
  const isSupported = integration.status === IntegrationListingStatus.Supported;
  const showViewCalls =
    integration.connected && normalizeAlphaNum(integration.name) === AGENCY_ZOOM_KEY;

  const handleCardClick = useCallback(() => {
    posthog.capture("integration_marketplace_card_clicked", {
      integration_id: integration.id,
      integration_name: integration.name,
      integration_status: integration.status,
      integration_category: integration.category,
      integration_connected: integration.connected,
    });

    if (integration.route && isSupported) {
      router.push(integration.route);
      return;
    }

    if (integration.connected) {
      onDisconnect();
      return;
    }

    if (isSupported) {
      onConnect();
    } else {
      onComingSoon();
    }
  }, [integration, isSupported, onConnect, onComingSoon, onDisconnect, router, posthog]);

  const handleCardKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick]
  );

  return (
    <div
      role="button"
      tabIndex={0}
      data-integration-id={integration.id}
      className={clsx(
        "group flex flex-col p-4 bg-white dark:bg-gray-800 rounded-xl transition-all duration-300 text-left",
        "shadow-sm hover:shadow-lg hover:-translate-y-0.5",
        "border border-gray-100 dark:border-gray-700/50",
        "hover:bg-gray-50/30 dark:hover:bg-gray-750/50 cursor-pointer",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-gailBlue",
        integration.connected && "border-green-200 dark:border-green-700/50",
        highlighted && "ring-2 ring-gailBlue border-gailBlue dark:border-gailBlue"
      )}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="flex items-center justify-between w-full mb-3">
        <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-600/30 flex items-center justify-center overflow-hidden group-hover:border-gray-200 dark:group-hover:border-gray-600 transition-colors">
          {!imgError && integration.logoUrl ? (
            <Image
              src={integration.logoUrl}
              alt={integration.name}
              width={24}
              height={24}
              unoptimized
              className="w-6 h-6 object-contain opacity-90 group-hover:opacity-100 transition-opacity"
              onError={() => setImgError(true)}
            />
          ) : (
            <Image
              src={getLetterAvatar(integration.name)}
              alt={integration.name}
              width={32}
              height={32}
              unoptimized
              className="w-8 h-8"
            />
          )}
        </div>
        <span
          className={clsx(
            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border",
            integration.connected
              ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700/50"
              : "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700/40 dark:text-gray-300 dark:border-gray-600/60"
          )}
          aria-label={integration.connected ? ct("connected") : ct("disconnected")}
        >
          <span
            className={clsx(
              "w-1.5 h-1.5 rounded-full",
              integration.connected ? "bg-green-500" : "bg-gray-500"
            )}
          />
          {integration.connected ? ct("connected") : ct("disconnected")}
        </span>
      </div>
      <h3 className="font-semibold text-gray-900 dark:text-white text-sm leading-snug">
        {integration.name}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1 leading-relaxed">
        {integration.description}
      </p>
      {showViewCalls ? (
        <Link
          href={AGENCY_ZOOM_CALLS_ROUTE}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-gailBlue hover:underline"
        >
          {t("calls.viewCalls")}
          <Icon icon="arrow-right" className="w-3 h-3" />
        </Link>
      ) : null}
    </div>
  );
}
